import { useMemo } from 'react';
import { useRouter } from 'next/router';

const useExplore = () => {
	const router = useRouter();

	const selectedCategories = useMemo(() => {
		return router?.query?.categories ? router.query.categories.split(',').map((val) => +val) : [];
	}, [router.query.categories]);

	const selectedPrice = useMemo(() => {
		return router?.query?.price_type ? router.query.price_type.split(',') : [];
	}, [router.query.price_type]);

	const selectedStatus = useMemo(() => {
		return router?.query?.status ? router.query.status.split(',') : [];
	}, [router.query.status]);

	const selectedProficiency = useMemo(() => {
		return router?.query?.proficiencies ? router.query.proficiencies.split(',') : [];
	}, [router.query.proficiencies]);

	const selectedContent = useMemo(() => {
		return router?.query?.content || '';
	}, [router.query.content]);

	const searchKeyword = useMemo(() => {
		return router?.query?.search_keyword || '';
	}, [router.query.search_keyword]);

	const selectedOrdering = useMemo(() => {
		return router?.query?.ordering || '';
	}, [router.query.ordering]);

	const selectedCareerPath = useMemo(() => {
		return router?.query?.career_path || '';
	}, [router.query.career_path]);

	const selectedPage = useMemo(() => {
		return router?.query?.page || '';
	}, [router.query.page]);

	const queryWithoutPage = () => {
		const { page, ...queryParams } = router.query;
		return queryParams;
	};

	const selectedHighlighted = useMemo(() => {
		return router?.query?.highlighted_learnings ? router.query.highlighted_learnings.split(',') : [];
	}, [router.query.highlighted_learnings]);

	const handleCategoryClick = (id) => {
		const queryParamsWithoutPage = queryWithoutPage();
		let newSelectedCategories;
		if (selectedCategories.includes(id)) {
			newSelectedCategories = selectedCategories.filter((catId) => catId !== id);
		} else {
			newSelectedCategories = [...selectedCategories, id];
		}
		if (newSelectedCategories.length > 0) {
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParamsWithoutPage, categories: newSelectedCategories.join(',') }
				},
				undefined,
				{ shallow: true }
			);
		} else {
			const { categories, ...queryParams } = queryParamsWithoutPage;
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParams }
				},
				undefined,
				{ shallow: true }
			);
		}
	};

	const handlePriceClick = (newPrice) => {
		const queryParamsWithoutPage = queryWithoutPage();
		let newSelectedPrice;
		if (selectedPrice.includes(newPrice)) {
			newSelectedPrice = selectedPrice.filter((priceType) => priceType !== newPrice);
		} else {
			newSelectedPrice = [...selectedPrice, newPrice];
		}
		if (newSelectedPrice.length > 0) {
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParamsWithoutPage, price_type: newSelectedPrice.join(',') }
				},
				undefined,
				{ shallow: true }
			);
		} else {
			const { price_type, ...queryParams } = queryParamsWithoutPage;
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParams }
				},
				undefined,
				{ shallow: true }
			);
		}
	};

	const handlePriceSelect = (newPrice) => {		
		const queryParamsWithoutPage = queryWithoutPage();		
		if(newPrice === 'all')
		{
			const { price_type, ...queryParams } = queryParamsWithoutPage;
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParams }
				},
				undefined,
				{ shallow: true }
			);
		}else {
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParamsWithoutPage, price_type: newPrice }
				},
				undefined,
				{ shallow: true }
			);
		}
	};

	const handleProficiencyClick = (newProficiency) => {
		const queryParamsWithoutPage = queryWithoutPage();
		let newSelectedProficiency;
		if (selectedProficiency.includes(newProficiency)) {
			newSelectedProficiency = selectedProficiency.filter((proficiency) => proficiency !== newProficiency);
		} else {
			newSelectedProficiency = [...selectedProficiency, newProficiency];
		}
		if (newSelectedProficiency.length > 0) {
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParamsWithoutPage, proficiencies: newSelectedProficiency.join(',') }
				},
				undefined,
				{ shallow: true }
			);
		} else {
			const { proficiencies, ...queryParams } = queryParamsWithoutPage;
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParams }
				},
				undefined,
				{ shallow: true }
			);
		}
	};

	const handleProficiencySelect = (newProficiency) => {		
		const queryParamsWithoutPage = queryWithoutPage();		
		if(newProficiency === 'all')
		{
			const { proficiencies, ...queryParams } = queryParamsWithoutPage;
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParams }
				},
				undefined,
				{ shallow: true }
			);
		}else {
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParamsWithoutPage, proficiencies: newProficiency }
				},
				undefined,
				{ shallow: true }
			);
		}
	};

	const handleStatusSelect = (newStatus) => {		
		const queryParamsWithoutPage = queryWithoutPage();		
		if(newStatus === 'all')
		{
			const { status, ...queryParams } = queryParamsWithoutPage;
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParams }
				},
				undefined,
				{ shallow: true }
			);
		}else {
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParamsWithoutPage, status: newStatus }
				},
				undefined,
				{ shallow: true }
			);
		}
	};

	const handleQueryKeywordChange = (newValue) => {
		const queryParamsWithoutPage = queryWithoutPage();
		if (newValue) {
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParamsWithoutPage, search_keyword: newValue }
				},
				undefined,
				{ shallow: true }
			);
		} else {
			const { search_keyword, ...queryParams } = queryParamsWithoutPage;
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParams }
				},
				undefined,
				{ shallow: true }
			);
		}
	};

	const handleContentTypeSelect = (newType) => {
		const queryParamsWithoutPage = queryWithoutPage();
		if (newType === selectedContent) {
			return;
		} else if (newType === 'all') {
			const { content, ...queryParams } = queryParamsWithoutPage;
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParams }
				},
				undefined,
				{ shallow: true }
			);
		} else {
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParamsWithoutPage, content: newType }
				},
				undefined,
				{ shallow: true }
			);
		}
	};

	const handleOrderingChange = (newOrdering) => {
		const queryParams = router.query;
		if (newOrdering === selectedOrdering) {
			return;
		} else {
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParams, ordering: newOrdering }
				},
				undefined,
				{ shallow: true }
			);
		}
	};

	const handleCareerPathChange = (newCareerPath) => {
		const queryParams = router.query;
		if (newCareerPath === selectedCareerPath) {
			return;	
		} else {
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParams, career_path: newCareerPath, page: 1 },					
				},
				undefined,
				{ shallow: true }
			);
		}
	};

	const handlePageChange = (newPage) => {
		const queryParams = router.query;
		if (newPage) {
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParams, page: newPage }
				},
				undefined,
				{ shallow: true }
			);
		} else {
			const { page, ...queryParams } = queryParams;
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParams }
				},
				undefined,
				{ shallow: true }
			);
		}
	};

	const handleHighlighted = (newHighlight) => {
		const queryParamsWithoutPage = queryWithoutPage();
		let newSelectedHighlight;
		if (selectedHighlighted.includes(newHighlight)) {
			newSelectedHighlight = selectedHighlighted.filter((highlighted_learnings) => highlighted_learnings !== newHighlight);
		} else {
			newSelectedHighlight = [...selectedHighlighted, newHighlight];
		}
		if (newSelectedHighlight.length > 0) {
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParamsWithoutPage, highlighted_learnings: newSelectedHighlight.join(',') }
				},
				undefined,
				{ shallow: true }
			);
		} else {
			const { highlighted_learnings, ...queryParams } = queryParamsWithoutPage;
			router.replace(
				{
					pathname: router.pathname,
					query: { ...queryParams }
				},
				undefined,
				{ shallow: true }
			);
		}
	};

	return {
		selectedCategories,
		selectedPrice,
		selectedStatus,
		selectedProficiency,
		selectedContent,
		searchKeyword,
		selectedOrdering,
		selectedCareerPath,
		selectedPage,
		selectedHighlighted,
		handleCategoryClick,
		handlePriceClick,
		handleProficiencyClick,
		handlePriceSelect,
		handleProficiencySelect,
		handleStatusSelect,
		handleQueryKeywordChange,
		handleContentTypeSelect,
		handleOrderingChange,
		handleCareerPathChange,
		handlePageChange,
		handleHighlighted
	};
};

export default useExplore;
